import {
  Component,
  computed,
  DestroyRef,
  inject,
  input, InputSignal, Signal,
  signal,
  viewChild,
  WritableSignal
} from '@angular/core';
import { Subject } from 'rxjs';
import { DualMonthDayPickerComponent } from '../dual-month-day-picker/dual-month-day-picker.component';
import { YearPickerComponent } from '../year-picker/year-picker.component';
import { MonthPickerComponent } from '../month-picker/month-picker.component';
import { DayPickerComponent } from '../day-picker/day-picker.component';
import { DatePickerInnerComponent } from '../date-picker-inner/date-picker-inner.component';
import { OverlayComponent } from '../../overlay/overlay.component';
import { IdService } from '../../../services/id.service';
import { OverlayService } from '../../overlay/overlay.service';
import { PlatformService } from '../../../services/platform.service';
import { DatePickerService } from './date-picker.service';
import { DatePickerConfig } from './date-picker.config';
import { DatePickerInterface } from './date-picker-interface';

import type { TemplateRef, OutputRefSubscription } from '@angular/core';
import type { DatePickerFormatType } from '../../../services/date/date-formatter.service';
import type { DatePickerMode } from './date-picker-mode.enum';
import type { DateValidator } from './date-validator.type';
import type { DatePickerInputLike } from './date-picker.contract';

/**
 * The Date Picker Component
 *
 * This is the input for selecting dates
 */
@Component({
  selector: 'td-wb-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
  providers: [
    DatePickerConfig,
    DatePickerService,
    { provide: DatePickerInterface, useExisting: DatePickerComponent }
  ],
  imports: [
    OverlayComponent,
    DatePickerInnerComponent,
    DayPickerComponent,
    MonthPickerComponent,
    YearPickerComponent,
    DualMonthDayPickerComponent
  ]
})
export class DatePickerComponent extends DatePickerInterface {
  private static readonly ID_PREFIX: string = 'td-wb-date-picker';

  // ── DI ──────────────────────────────────────────────────────────────────
  private readonly config = inject(DatePickerConfig);
  private readonly datePickerService = inject(DatePickerService);
  private readonly overlayService = inject(OverlayService);
  private readonly idService = inject(IdService);
  private readonly platformService = inject(PlatformService);
  private readonly destroyRef = inject(DestroyRef);

  // ── Signal inputs ────────────────────────────────────────────────────────
  /**
   * Sets datepicker mode, supports: day, month, year; defaults to day
   */
  readonly mode: InputSignal<DatePickerMode> = input<DatePickerMode>(this.config.mode);

  /**
   * Sets minimum datepicker mode, supports: day, month, year; defaults to day
   */
  readonly minMode: InputSignal<string> = input<string>(this.config.minMode);

  /**
   * Sets maximum datepicker mode, supports: day, month, year; defaults to year
   */
  readonly maxMode: InputSignal<string> = input<string>(this.config.maxMode);

  /**
   * if false week numbers will be hidden; defaults to false
   */
  readonly showWeeks: InputSignal<boolean> = input<boolean>(this.config.showWeeks);

  /**
   * Label of the input element
   */
  readonly label: InputSignal<string> = input<string>('');

  /**
   * Id of the input element
   */
  readonly datePickerId: InputSignal<string | undefined> = input<string | undefined>(undefined);

  /**
   * If true only dates of current month will be displayed.
   */
  readonly displayDatesOfCurrentMontyOnly: InputSignal<boolean> = input<boolean>(false);

  // ── ViewChild ────────────────────────────────────────────────────────────
  readonly content: Signal<TemplateRef<unknown> | undefined> = viewChild<TemplateRef<unknown>>('picker');

  // ── Config-derived props (set from DatePickerConfig) ─────────────────────
  formatDay: DatePickerFormatType = this.config.formatDay as DatePickerFormatType;
  formatMonth: DatePickerFormatType = this.config.formatMonth as DatePickerFormatType;
  formatYear: DatePickerFormatType = this.config.formatYear as DatePickerFormatType;
  formatDayHeader: DatePickerFormatType = this.config.formatDayHeader as DatePickerFormatType;
  formatDayTitle: DatePickerFormatType = this.config.formatDayTitle as DatePickerFormatType;
  formatMonthTitle: DatePickerFormatType = this.config.formatMonthTitle as DatePickerFormatType;
  startingDay: number = this.config.startingDay;
  yearRange: number = this.config.yearRange;

  // ── Internal state ───────────────────────────────────────────────────────
  /** Signal-backed value so [parsedDate]="getParsed()" is reactive without Zone ticks. */
  private readonly _value: WritableSignal<Date | null> = signal<Date | null>(null);

  /** Expose value as plain property for ControlValueAccessor / forms compat reading it imperatively. */
  get value(): Date | null { return this._value(); }
  set value(v: Date | null) { this._value.set(v); }

  platformIOS: boolean = false;

  /**
   * @deprecated Use `output()` / subscribe at component level. Kept for backwards compat.
   */
  readonly selectedChanged: Subject<Date> = new Subject<Date>();

  private datepickerInput: DatePickerInputLike;
  private inputSubscription: OutputRefSubscription | null = null;

  constructor() {
    super();
    this.uniqueId = this.idService.generateNamespaceSequentialId(DatePickerComponent.ID_PREFIX);
    this.platformIOS = this.platformService.isIOS();
  }

  get format(): DatePickerFormatType {
    return this.datepickerInput === undefined ? null : this.datepickerInput.format();
  }

  get minDate(): Date | null {
    return this.datepickerInput === undefined ? null : this.datepickerInput.minDate();
  }

  get maxDate(): Date | null {
    return this.datepickerInput === undefined ? null : this.datepickerInput.maxDate();
  }

  get disabled(): boolean {
    return this.datepickerInput === undefined ? false : this.datepickerInput.disabled;
  }

  get datePickerInputId(): string {
    return this.datepickerInput !== undefined && this.datepickerInput.id !== undefined ? this.datepickerInput.id : '';
  }

  /**
   * Computed parsed date — reactive, so [parsedDate]="getParsed()" in templates
   * updates automatically when value changes via ngModel / writeValue.
   */
  readonly getParsed: Signal<Date | null> = computed(() =>
    this.datePickerService.isDate(this._value()) ? this._value() : null
  );

  /**
   * This function is called when date is selected from date picker component
   */
  onDatePickerChange(date: Date): void {
    this.selectedChanged.next(date);
    this.overlayService.close();
  }

  /**
   * Returns isDisabledDate function
   */
  disabledDateInput(): DateValidator {
    return (date: Date): boolean => this.datepickerInput.isDisabledDate(date);
  }

  registerInput(input: DatePickerInputLike): void {
    if (this.inputSubscription) {
      this.inputSubscription.unsubscribe();
    }
    this.datepickerInput = input;
    // Immediately sync the current value (in case writeValue was called before registerInput).
    this._value.set(input.value);
    this.inputSubscription =
      this.datepickerInput.valueChange
        .subscribe((value: Date | null): void => {
          this._value.set(value);
        });
    this.destroyRef.onDestroy(() => this.inputSubscription?.unsubscribe());
  }
}
